import React, { useMemo, useState } from 'react';
import { BadgeCheck, Settings as Cog } from 'lucide-react';
import { useProducerPage } from './producerPage/useProducerPage.js';
import { useAssetUrl } from './producerPage/useAssetUrl.js';
import Stepper from './producerPage/Stepper.jsx';
import PreviewFrame from './producerPage/PreviewFrame.jsx';
import { btnGhost, btnPrimary } from './producerPage/ui.jsx';
import TitleStep from './producerPage/steps/TitleStep.jsx';
import AboutStep from './producerPage/steps/AboutStep.jsx';
import ProductsStep from './producerPage/steps/ProductsStep.jsx';
import StatePicker from './producerPage/steps/StatePicker.jsx';
import LicenseUploads from './producerPage/steps/LicenseUploads.jsx';
import CarriersStep from './producerPage/steps/CarriersStep.jsx';
import ContactStep from './producerPage/steps/ContactStep.jsx';
import ReviewStep from './producerPage/steps/ReviewStep.jsx';
import ProducerPageView from '../../components/producerPage/ProducerPageView.jsx';
import { STEPS, canContinue, previewModel, previewAddress } from '../../lib/producerPage.js';

const STEP_VIEWS = { title: TitleStep, about: AboutStep, products: ProductsStep, carriers: CarriersStep, contact: ContactStep, review: ReviewStep };
const PREVIEW_ONLY = { title: 'title', about: 'about', products: 'products', licenses: 'licenses', carriers: 'carriers', contact: 'contact', review: undefined };

/**
 * Settings → Producer Page: the 7-step wizard that builds the agent's public
 * "Producer Validation Page" (optional; also reachable from onboarding + dashboard).
 * @returns {JSX.Element}
 */
export default function ProducerPageTab() {
  const ctx = useProducerPage();
  const [step, setStep] = useState(0);
  const [licensePhase, setLicensePhase] = useState('pick'); // licenses step: 'pick' states, then 'upload' copies
  const { page, assets, catalog, slug, server, rev, busy } = ctx;

  const profileUrl = useAssetUrl('profile', '', !!assets.profile, rev);
  const bgUrl = useAssetUrl('background', '', !!assets.background, rev);
  const model = useMemo(
    () => (page ? previewModel(page, catalog, assets, { profile: profileUrl, background: bgUrl }) : null),
    [page, catalog, assets, profileUrl, bgUrl],
  );

  if (ctx.error) return <div className="panel p-6 text-sm text-rose-300">Could not load the producer page setup. Please refresh.</div>;
  if (ctx.loading || !model) return <div className="panel p-6 text-sm text-white/60">Loading…</div>;

  const id = STEPS[step].id;
  const last = step === STEPS.length - 1;
  const ok = canContinue(id, { page, assets, slug, licensePhase });
  const Body = id === 'licenses' ? (licensePhase === 'pick' ? StatePicker : LicenseUploads) : STEP_VIEWS[id];

  async function next() {
    if (!(await ctx.save({ quiet: true }))) return;
    if (id === 'licenses' && licensePhase === 'pick') { setLicensePhase('upload'); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }
  function back() {
    if (id === 'licenses' && licensePhase === 'upload') { setLicensePhase('pick'); return; }
    setStep((s) => Math.max(s - 1, 0));
  }
  function go(i) {
    if (STEPS[i].id === 'licenses') setLicensePhase('pick');
    setStep(i);
  }

  return (
    <div className="space-y-4" data-testid="producer-page-tab">
      <div className="panel p-4">
        <div className="flex items-center gap-2 font-semibold text-civix-purpleSoft"><Cog className="h-4 w-4" /> Welcome to producer page setup!</div>
        <p className="mt-1 text-xs text-white/70">
          Set up your personalized producer page with a title, picture and licenses so your leads have a place to see all of your credentials. This is optional, and helps you build trust and verify your identity with your customers.
        </p>
      </div>

      <div className="panel p-4 sm:p-5">
        <div className="mb-5 flex items-center gap-3 border-b border-white/10 pb-4">
          <BadgeCheck className="h-5 w-5 shrink-0 text-civix-purpleSoft" />
          <Stepper current={step} onGo={go} />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="min-w-0"><Body ctx={ctx} /></div>

          <div className="min-w-0 space-y-3">
            <PreviewFrame address={previewAddress(server?.url, page.slug)} title={page.title}>
              {id === 'contact' && !page.showQuoteForm
                ? <p className="p-8 text-center text-xs text-slate-400">The “Request a Quote” section is hidden on your page.</p>
                : <ProducerPageView model={model} only={PREVIEW_ONLY[id]} />}
            </PreviewFrame>
            <div className="flex justify-end gap-2">
              <button type="button" className={btnGhost} onClick={back} disabled={busy || (step === 0)} data-testid="pp-prev">Previous section</button>
              <button type="button" className={btnPrimary} onClick={next} disabled={busy || !ok || last} data-testid="pp-next">Next section</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
